import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ENTITY_TYPES,
  useEntities,
  type EntityTypeSlug,
} from '../api/queries/entities';
import { FilterableSelect } from '../components/common/FilterableSelect';
import { useImportSharedEntity } from '../api/queries/libraries';

export function SharedLibraryRoute() {
  const [type, setType] = useState<EntityTypeSlug | ''>('');
  const [search, setSearch] = useState('');
  const query = useEntities({
    scope: 'shared',
    type: type || undefined,
    search: search.trim() || undefined,
    pageSize: 100,
  });
  const importMutation = useImportSharedEntity();
  const [lastImportedId, setLastImportedId] = useState<string | null>(null);

  if (query.isError) {
    return <Alert severity="error">Failed to load shared library.</Alert>;
  }

  return (
    <div>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Shared library
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Admin-curated entities with approved LoRA models. Import a copy into
        your private library to use in your books.
      </Typography>

      {lastImportedId && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setLastImportedId(null)}
        >
          Imported into your private library.
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap">
        <FilterableSelect
          label="Type"
          value={type}
          onChange={(newValue) =>
            setType((newValue || '') as EntityTypeSlug | '')
          }
          allowEmpty={true}
          emptyLabel="All types"
          options={ENTITY_TYPES.map((t) => ({
            value: t.value,
            label: t.label,
          }))}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
      </Stack>

      {query.isLoading && <CircularProgress />}

      <Stack spacing={1}>
        {(query.data?.items ?? []).map((e) => (
          <Card key={e.id}>
            <CardActionArea disableRipple>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" sx={{ flex: 1 }}>
                    {e.name}
                  </Typography>
                  <Chip size="small" label={e.entityType} />
                  {e.hasApprovedLora && (
                    <Chip size="small" color="success" label="LoRA" />
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    disabled={importMutation.isPending}
                    onClick={async () => {
                      const result = await importMutation.mutateAsync(e.id);
                      setLastImportedId(result.id);
                    }}
                  >
                    Copy to private
                  </Button>
                </Stack>
                {e.description && (
                  <Typography variant="body2" color="text.secondary">
                    {e.description.slice(0, 200)}
                  </Typography>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
        {query.data && query.data.items.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No shared entities available yet.
          </Typography>
        )}
      </Stack>
    </div>
  );
}
